/**
 * DocumentPortal (E4, rebuilt in E4b) — the PUBLIC, token-authorized shell for
 * every non-estimate portal document. Rendered outside the auth wrapper;
 * authorization is the token in the URL and nothing else.
 *
 * This file is the SHELL only: fetch, error states, branding, and the choice of
 * view. The layouts live one per file under ./portal, and the two contractor
 * documents deliberately share ONE component (ContractorDocView) because they
 * are the same document with two differences.
 *
 * PRIVACY: the contractor ESTIMATE payload contains no client name, phone, email
 * or street address — routes/portal.ts withholds them server-side, so no view
 * here is capable of leaking them.
 */

import React, { useEffect, useState } from 'react';
import { formatPhone, telHref } from '../lib/format';
import { Icons } from '../lib/icons';
import { PORTAL_CSS } from './portal/portalStyles';
import { ContractorDocView } from './portal/ContractorDocView';
import { InvoiceView } from './portal/InvoiceView';
import { InventoryView } from './portal/InventoryView';
import { InventoryRequestView } from './portal/InventoryRequestView';
import { TemplateDocumentView } from './portal/TemplateDocumentView';
import { SigningView } from './portal/SigningView';
import { FeedbackView } from './portal/FeedbackView';
import { usePortalReady } from './portal/portalReady';
import { isPrintMode } from './portal/downloadPdf';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
// SELF-HOSTED, same origin as the portal. It used to be hotlinked from
// balticvanlines.ca and rendered as alt text inside the PDF on Railway — while
// loading fine from a browser and from curl, and fine in headless Chromium
// locally (300x178). That combination means the failure was specific to the
// container's egress, not to the URL, the format, or CORS.
//
// Rather than keep guessing, the dependency is removed: a client's invoice
// should not require a third-party WordPress host to be reachable at the moment
// the PDF is generated. Same-origin also means it is already warm in the cache
// the portal was just served from.
const PHONE = '647 931 5244';

export function DocumentPortal({ token }: { token: string }) {
  const [data, setData] = useState<any | null>(null);
  /**
   * TWO FAILURES, NOT ONE.
   *
   * Every failure rendered the same page — "This link is no longer available…
   * the document may have been voided or the link revoked" — so a link that was
   * never valid told the client their document had been cancelled. That is a
   * false statement to a customer, produced by a wiring mistake on our side,
   * and it sent them to the phone to ask about a void that never happened.
   *
   * 'gone'    the server said 410: revoked, or the document really is void.
   * 'invalid' anything else — a 404 from a bad or mistyped token, or the
   *           request not completing at all. We do not know that anything was
   *           revoked, so we do not say so.
   */
  const [status, setStatus] = useState<'loading' | 'ok' | 'gone' | 'invalid'>('loading');
  const [approving, setApproving] = useState(false);
  const [approvedAt, setApprovedAt] = useState<string | null>(null);

  const [signing, setSigning] = useState<any | null>(null);

  /**
   * The signing payload, fetched only for template documents. It is a SEPARATE
   * request rather than more fields on the document payload because it records
   * a 'viewed' audit event — and every other portal type would then be logging
   * views of documents that have nothing to view.
   *
   * A failure here is not fatal: the read-only view still works, so a client can
   * always at least see and download their document.
   *
   * NEVER IN PRINT MODE. The PDF pipeline generates documents by driving THIS
   * page in Chromium (see backend/src/lib/pdf.ts), so without this guard every
   * internal PDF would fire the request — and that endpoint stamps a 'viewed'
   * signature event. Staff pulling a copy would record that the CLIENT had seen
   * the document, which is the evidence the completion certificate rests on.
   * Returning null also keeps the PDF rendering TemplateDocumentView, the
   * read-only form it has always been, rather than the interactive signing page.
   */
  const loadSigning = React.useCallback(async () => {
    if (isPrintMode()) return null;
    try {
      const res = await fetch(`${API_BASE_URL}/portal/document/${encodeURIComponent(token)}/signing`);
      if (!res.ok) return null;
      const body = await res.json();
      return (body?.data?.blocks?.length ?? 0) > 0 ? body.data : null;
    } catch {
      return null;
    }
  }, [token]);

  /**
   * Refresh after a mark is applied, so the server-filled html replaces what the
   * page loaded with.
   *
   * KEEPS THE OLD PAYLOAD ON FAILURE. loadSigning() returns null for any error,
   * and null is also how this component says "not a signing document" — so
   * assigning the result straight through would turn one dropped request into
   * the signer being thrown out of the signing view and into the read-only one,
   * mid-signature, with no error and no way back except a reload.
   */
  const reloadSigning = React.useCallback(() => {
    loadSigning().then((next) => { if (next) setSigning(next); });
  }, [loadSigning]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/portal/document/${encodeURIComponent(token)}`);
        if (!res.ok) {
          if (!cancelled) setStatus(res.status === 410 ? 'gone' : 'invalid');
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        setData(body.data);
        setApprovedAt(body.data?.contractorDoc?.approvedAt ?? null);
        if (body.data?.targetType === 'template_document') {
          const sg = await loadSigning();
          if (!cancelled) setSigning(sg);
        }
        setStatus('ok');
      } catch {
        // A THROW IS NOT A REVOCATION. A dropped connection or a parse failure
        // tells us nothing about the document's state.
        if (!cancelled) setStatus('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token, loadSigning]);

  const approve = async () => {
    setApproving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/portal/document/${encodeURIComponent(token)}/approve`, { method: 'POST' });
      if (!res.ok) throw new Error('approve failed');
      const body = await res.json();
      setApprovedAt(body.data?.approvedAt ?? new Date().toISOString());
    } catch {
      alert('Sorry — something went wrong recording your approval. Please call us at ' + PHONE + '.');
    } finally {
      setApproving(false);
    }
  };

  // P4 fix — tell the PDF renderer when this page has actually painted its data.
  // `status` is the view's own settle state, so the signal cannot drift from
  // what is on screen.
  usePortalReady(status === 'loading' ? 'loading' : status === 'ok' ? 'ok' : 'error');

  const t = data?.targetType;
  // P4: every view downloads from the same token-authorized endpoint, so the
  // path is derived once here rather than assembled in three places.
  const pdfPath = `/portal/document/${encodeURIComponent(token)}/pdf`;

  // Contractor documents print their own header and a running footer (the
  // PDF's), so the portal bar and foot — with their tel: links — stay off paper.
  return (
    <div className={`pp${t === 'contractor_work_order' || t === 'contractor_estimate' ? ' pp-doc-contractor' : ''}`}>
      <style>{PORTAL_CSS}</style>

      <header className="pp-bar">
        <div className="pp-bar-in">
          <div>
            <img src="/brand/logo-horizontal-notag-white.svg" alt="Baltic Van Lines" className="pp-logo pp-logo-screen" />
            <img src="/brand/logo-horizontal-notag-color.svg" alt="Baltic Van Lines" className="pp-logo pp-logo-print" />
          </div>
          <a href={`tel:${PHONE.replace(/\s/g, '')}`} className="pp-tel">
            <Icons.phone size={14} aria-hidden="true" style={{ verticalAlign: 'text-bottom' }} /> {PHONE}
          </a>
        </div>
      </header>

      {status === 'loading' && <div className="pp-mid">Loading…</div>}

      {status === 'gone' && (
        <div className="pp-mid">
          <h2>This link is no longer available</h2>
          <p>The document may have been voided or the link revoked. Please contact us and we'll be glad to help.</p>
          <a href={`tel:${PHONE.replace(/\s/g, '')}`} className="pp-btn primary" style={{ display: 'inline-flex', flex: '0 0 auto' }}>Call {PHONE}</a>
        </div>
      )}

      {status === 'invalid' && (
        <div className="pp-mid">
          <h2>This link isn't valid</h2>
          <p>We couldn't open a document from this link. It may have been copied incompletely. Please contact us and we'll send you a new one.</p>
          <a href={`tel:${PHONE.replace(/\s/g, '')}`} className="pp-btn primary" style={{ display: 'inline-flex', flex: '0 0 auto' }}>Call {PHONE}</a>
        </div>
      )}

      {status === 'ok' && data && (
        <main className="pp-main">
          {(t === 'contractor_work_order' || t === 'contractor_estimate') && (
            <ContractorDocView data={data} approvedAt={approvedAt} approving={approving} onApprove={approve} pdfPath={pdfPath} />
          )}

          {t === 'invoice' && <InvoiceView data={data} pdfPath={pdfPath} />}

          {t === 'inventory' && <InventoryView data={data} pdfPath={pdfPath} />}

          {/* A template document is either something to READ or something to
              SIGN, and which one it is comes from the document itself: it has
              input blocks and has been sent for signature. Guessing from the
              status would get it wrong for a document sent as a plain copy. */}
          {t === 'template_document' && (
            signing
              ? <SigningView data={signing} token={token} pdfPath={pdfPath} onReload={reloadSigning} />
              : <TemplateDocumentView data={data} pdfPath={pdfPath} />
          )}

          {/* P5a's client-facing inventory SUBMISSION flow — a different thing
              from the read-only inventory view above. That one is the
              CONTRACTOR's copy and carries cuft and weight; this one is the
              client's and carries neither. */}
          {t === 'inventory_request' && data.inventoryRequest && (
            <InventoryRequestView inventory={data.inventoryRequest} token={token} />
          )}

          {/* 073: the post-move feedback page — rating, then its result screen. */}
          {t === 'feedback' && data.feedback && <FeedbackView feedback={data.feedback} token={token} />}

          <div className="pp-foot">
            {data.company?.name}
            {data.company?.phone ? <> · <a href={telHref(data.company.phone)}>{formatPhone(data.company.phone)}</a></> : null}
            {data.company?.email ? ` · ${data.company.email}` : ''}
          </div>
        </main>
      )}
    </div>
  );
}
